import React, { InputHTMLAttributes } from 'react';
import { CurrencyType, FormatCurrencyValue, formatCurrencyValue } from '../../utils/currency';

/**
 * Renders a numberical input that formats the input based on a sepcified currency type.
 * Display formatting and validation is performed onBlur.
 * (Input value is formatted onChange for use by onValueChange callback)
 */

/**
 * Interface for return value for On Change
 * name: name of element (used for differentiating between multiple inputs)
 * string: non-formatted string of input value
 * formatted: formatted string of input value
 * float: numerical value of input value
 */
export interface GACurrencyInputOnChangeValues {
    name: string | undefined,
    string?: string,
    formatted?: string,
    float?: number
}

/**
 * Props for currency Input
 * currency: CurrencyType for input
 * (TODO: make this an array of currency types)
 * onValueChange: Called when the value of the input is adjusted
 * NOTE: onValueChange does not enforce validation.
 * onInputBlur: Called onBlur. (TODO: Override onBlur HTML Input prop)
 * NOTE: onImputBlur does enforce validation.
 * onFocus: Called when the input is focused
 * max: Maximum value. Enforced onBlur.
 * min: Minimum value. Enforced onBlur.
 * defaultValue: Default Value of input
 * name: Name of input (used for identification)
 * className: CSS Class Name for input
 */
interface GACurrencyInputProps extends InputHTMLAttributes<HTMLInputElement>
{
    currency: CurrencyType,
    onValueChange?: (arg: GACurrencyInputOnChangeValues) => void,
    onInputBlur?: (arg: GACurrencyInputOnChangeValues) => void,
    onFocus?: () => void,
    max?: number,
    min?: number,
    defaultValue?: string | number
    name: string,
    className?: string,
    disabled?: boolean
}

/**
 * Main Component
 */
const GACurrencyInput = (props: GACurrencyInputProps) => 
{
    const formatting = 
    {
        prefix: props.currency.formatting.prefix,
        suffix: props.currency.formatting.suffix,
        groupSeparator: props.currency.formatting.groupSeparator,
        decimalSeparator: props.currency.formatting.decimalSeparator || '.',
        decimalLimit: (props.currency.formatting.decimalLimit === undefined) ? 2: props.currency.formatting.decimalLimit
    }

    const allowedCharacters = RegExp('/([0-9.-]*)$/');

    const maxValue = React.useMemo(() => formatCurrencyValue(props.max ? props.max : 0, formatting), [props.max]);
    const minValue = React.useMemo(() => formatCurrencyValue(props.min ? props.min : 0, formatting), [props.min]);

    const [value, setValue] = React.useState<FormatCurrencyValue>(props.defaultValue ? formatCurrencyValue(props.defaultValue, formatting) : {});
    const [toDisplay, setToDisplay] = React.useState<string | undefined>(props.defaultValue ? formatCurrencyValue(props.defaultValue, formatting).formatted : '');

    const handleOnFocus = () => 
    {
        //Clear Display if 0 or null value is displayed
        if(value.float === 0 || value.float === undefined)
        {
            setValue({});
            setToDisplay('');
        }
        props.onFocus && props.onFocus();
    }

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {

        const newValue = event.target.value;

        setToDisplay(newValue);

        const formatted = formatCurrencyValue(newValue, formatting);

        setValue(formatted);

        props.onValueChange && props.onValueChange({name: props.name, ...formatted});
    };

    //TODO: Refactor to avoid repitition
    const handleOnBlur = () => 
    {
        //Handle Bad String
        if(value.string === undefined || allowedCharacters.test(value?.string))
        {
            setToDisplay('');
        }
        else if((props.max != undefined) && value.float && value.float > props.max) 
        {
            setValue(maxValue);
            setToDisplay(maxValue.formatted);
            props.onInputBlur && props.onInputBlur({name: props.name, ...maxValue});
        }
        else if((props.min != undefined) && value.float && value.float < props.min) 
        {
            setValue(minValue);
            setToDisplay(minValue.formatted);
            props.onInputBlur && props.onInputBlur({name: props.name, ...minValue});
        }
        else
        {
            (value.formatted === undefined) ? setToDisplay('') : setToDisplay(value.formatted);
            props.onInputBlur && props.onInputBlur({name: props.name, ...value});
        }
    }

    return(
        <input
            name={props.name}
            className={props.className}
            type={'text'}
            inputMode={'decimal'}
            value={toDisplay}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            disabled={props.disabled ?? false}
        />
    )
}

export default GACurrencyInput;